/**
 * Phase 163 — Comparative Side-by-Side Scouting + AI Verdict
 *
 * Pick 2-5 jerseys → overlay their KPI curves on a single chart with
 * player-color-coded lines + an AI verdict banner that ranks them by
 * composite trend slope. The premium hook for district selectors and
 * multi-academy chains evaluating talent across roster movements.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  Sparkles, Award, Hash, TrendingUp, TrendingDown, Activity, X, Trophy,
} from 'lucide-react';

// Color palette — high-contrast, distinct, color-blind friendly
const PLAYER_COLORS = ['#a855f7', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444'];

const METRICS = [
  { key: 'xg',            label: 'xG' },
  { key: 'pass_accuracy', label: 'Pass Acc %' },
  { key: 'tackles_won',   label: 'Tackles Won %' },
  { key: 'possession',    label: 'Possession %' },
];

const TREND_LABEL_META = {
  rising_fast:  { text: 'Rising Fast', color: 'text-emerald-700 bg-emerald-100', Icon: TrendingUp },
  rising:       { text: 'Rising',      color: 'text-emerald-700 bg-emerald-50',  Icon: TrendingUp },
  flat:         { text: 'Flat',        color: 'text-slate-600 bg-slate-100',     Icon: Activity },
  falling:      { text: 'Falling',     color: 'text-rose-700 bg-rose-50',        Icon: TrendingDown },
  falling_fast: { text: 'Falling Fast', color: 'text-rose-700 bg-rose-100',      Icon: TrendingDown },
};

export default function ScoutingComparisonChart() {
  const [roster, setRoster] = useState([]);
  const [selectedJerseys, setSelectedJerseys] = useState([]); // array of {jersey_number, color}
  const [data, setData] = useState(null);
  const [activeMetric, setActiveMetric] = useState('xg');
  const [loading, setLoading] = useState(false);

  // Load the roster on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/video-analysis/scouting-roster');
        setRoster(r.data?.roster || []);
      } catch (_e) { /* silent */ }
    })();
  }, []);

  // Refire comparison whenever the selected jerseys change
  const loadComparison = useCallback(async () => {
    if (selectedJerseys.length < 2) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const csv = selectedJerseys.map((j) => j.jersey_number).join(',');
      const r = await api.get(
        `/admin/video-analysis/scouting-comparison?jersey_numbers=${encodeURIComponent(csv)}`
      );
      setData(r.data);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  }, [selectedJerseys]);

  useEffect(() => { loadComparison(); }, [loadComparison]);

  const addJersey = (jersey_number) => {
    if (selectedJerseys.some((j) => j.jersey_number === jersey_number)) return;
    if (selectedJerseys.length >= 5) return;
    const usedColors = new Set(selectedJerseys.map((j) => j.color));
    const color = PLAYER_COLORS.find((c) => !usedColors.has(c)) || PLAYER_COLORS[0];
    setSelectedJerseys((p) => [...p, { jersey_number, color }]);
  };

  const removeJersey = (jersey_number) => {
    setSelectedJerseys((p) => p.filter((j) => j.jersey_number !== jersey_number));
  };

  // Build chart rows: align timelines by match index so #10 match-1 lines up
  // with #7 match-1 visually. Longest timeline wins, shorter ones leave gaps.
  const chartRows = useMemo(() => {
    if (!data?.comparison_matrix) return [];
    const matrix = data.comparison_matrix;
    const maxLen = Math.max(0, ...Object.values(matrix).map((t) => t.length));
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const row = { idx: i + 1, label: `M${i + 1}` };
      for (const [jersey, timeline] of Object.entries(matrix)) {
        const point = timeline[i];
        if (point) row[jersey] = point[activeMetric];
      }
      rows.push(row);
    }
    return rows;
  }, [data, activeMetric]);

  const verdict = data?.ai_verdict;
  const verdictJerseyMeta = useMemo(() => {
    if (!verdict?.recommended_jersey) return null;
    return selectedJerseys.find((j) => j.jersey_number === verdict.recommended_jersey);
  }, [verdict, selectedJerseys]);

  const eligibleRoster = roster.filter(
    (r) => !selectedJerseys.some((j) => j.jersey_number === r.jersey_number)
  );

  return (
    <Card className="border-amber-200/60 bg-gradient-to-br from-amber-50/30 via-white to-orange-50/20"
      data-testid="phase163-comparison-chart">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-semibold text-base sm:text-lg" data-testid="phase163-title">
                  Comparative Scouting + AI Verdict
                </h2>
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold uppercase tracking-wider">Phase 163</Badge>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px] font-bold uppercase tracking-wider">
                  <Sparkles className="h-2.5 w-2.5 mr-1" /> Legend Tier
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="phase163-subtitle">
                Pick 2–5 jerseys → linear-regression composite slope picks the strongest growth trajectory.
              </p>
            </div>
          </div>
        </div>

        {/* Jersey picker */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2" data-testid="phase163-selected-jerseys">
            {selectedJerseys.length === 0 && (
              <span className="text-[12px] text-slate-500">Add at least 2 jerseys below to compare.</span>
            )}
            {selectedJerseys.map((j) => (
              <span
                key={j.jersey_number}
                data-testid={`phase163-selected-pill-${j.jersey_number}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-white"
                style={{ background: j.color }}
              >
                <Hash className="h-3 w-3" /> {j.jersey_number}
                <button
                  type="button"
                  onClick={() => removeJersey(j.jersey_number)}
                  className="opacity-80 hover:opacity-100"
                  aria-label={`Remove ${j.jersey_number}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          {selectedJerseys.length < 5 && eligibleRoster.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Add:</span>
              {eligibleRoster.slice(0, 8).map((r) => (
                <button
                  key={`${r.jersey_number}-${r.sport}`}
                  type="button"
                  onClick={() => addJersey(r.jersey_number)}
                  data-testid={`phase163-roster-add-${r.jersey_number}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-amber-50 hover:border-amber-300 transition-colors"
                >
                  <Hash className="h-2.5 w-2.5" /> {r.jersey_number}
                  <span className="text-[9px] text-slate-400">·{r.match_count}m</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Metric tab */}
        <div className="flex flex-wrap gap-1.5" data-testid="phase163-metric-tabs">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setActiveMetric(m.key)}
              data-testid={`phase163-metric-tab-${m.key}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${
                activeMetric === m.key
                  ? 'bg-amber-600 border-amber-600 text-white shadow-sm'
                  : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* AI Verdict banner */}
        {verdict && verdict.recommended_jersey && (
          <div
            className="rounded-xl p-3 sm:p-4 flex items-start gap-3 border-2 shadow-sm"
            style={{
              borderColor: verdictJerseyMeta?.color || '#a855f7',
              background: `linear-gradient(135deg, ${verdictJerseyMeta?.color || '#a855f7'}12, white)`,
            }}
            data-testid="phase163-verdict-banner"
          >
            <div
              className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: verdictJerseyMeta?.color || '#a855f7' }}
            >
              <Award className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">🤖 AI Verdict</span>
                <Badge
                  className="text-[10px] font-bold border-0"
                  style={{ background: verdictJerseyMeta?.color || '#a855f7', color: 'white' }}
                  data-testid="phase163-verdict-jersey"
                >
                  Promote {verdict.recommended_jersey}
                </Badge>
              </div>
              <p className="text-xs text-slate-700 leading-relaxed" data-testid="phase163-verdict-rationale">
                {verdict.verdict_rationale}
              </p>
            </div>
          </div>
        )}
        {verdict && !verdict.recommended_jersey && selectedJerseys.length >= 2 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800"
            data-testid="phase163-verdict-insufficient">
            {verdict.verdict_rationale}
          </div>
        )}

        {/* Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 min-h-[300px]" data-testid="phase163-chart-canvas">
          {selectedJerseys.length < 2 && (
            <div className="h-72 flex flex-col items-center justify-center gap-2 text-center px-6">
              <Trophy className="h-8 w-8 text-slate-300" />
              <div className="text-sm font-semibold text-slate-700">Pick at least 2 jerseys to start comparing.</div>
              <div className="text-xs text-slate-500 max-w-md">The linear-regression engine needs 2+ players × 2+ matches each to produce a meaningful AI verdict.</div>
            </div>
          )}
          {selectedJerseys.length >= 2 && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartRows} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {selectedJerseys.map((j) => (
                  <Line
                    key={j.jersey_number}
                    type="monotone"
                    dataKey={j.jersey_number}
                    name={j.jersey_number}
                    stroke={j.color}
                    strokeWidth={2.5}
                    dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Per-jersey trend cards */}
        {data?.trend_scores && Object.keys(data.trend_scores).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="phase163-trend-strip">
            {selectedJerseys.map((j) => {
              const s = data.trend_scores[j.jersey_number];
              if (!s) return null;
              const trendMeta = TREND_LABEL_META[s.trend_label] || TREND_LABEL_META.flat;
              const TrendIcon = trendMeta.Icon;
              return (
                <div
                  key={j.jersey_number}
                  data-testid={`phase163-trend-card-${j.jersey_number}`}
                  className="bg-white border-l-4 rounded-lg px-3 py-2.5"
                  style={{ borderLeftColor: j.color }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-slate-800">{j.jersey_number}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${trendMeta.color}`}>
                      <TrendIcon className="h-2.5 w-2.5" /> {trendMeta.text}
                    </span>
                    <span className="text-[10px] text-slate-400 ml-auto">{s.match_count}m</span>
                  </div>
                  <div className="text-[11px] text-slate-600 leading-tight">
                    xG <strong className="text-slate-800">{s.xg >= 0 ? '+' : ''}{s.xg}</strong> / match ·
                    Pass <strong className="text-slate-800">{s.pass_accuracy >= 0 ? '+' : ''}{s.pass_accuracy}</strong> ·
                    Composite <strong style={{ color: s.composite >= 0 ? '#059669' : '#dc2626' }}>{s.composite >= 0 ? '+' : ''}{s.composite}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
